import React, { useEffect, useRef, useState } from "react";
import FullScreenPanel from "../../ui/FullScreenPanel.jsx";
import { phoneUi } from "../../ui/phoneUi";
import SnapshotImg from "../../../components/SnapshotImg";
import { CAMERA_NAMES, getCfHeaders, snapshotUrl, streamUrl } from "../../../utils/cameraRelay";
import { loadLaserConsumables } from "../../../features/queue/queueData";
import { emergencyStop, resetEmergencyStop } from "../../../features/queue/taskActions";

// The Laser on a phone is a monitor (plan: "Laser: monitor, Pause/Resume, E-stop/reset, as
// today"): the three cameras, the remote E-stop and reset, and the consumables' life. Pause /
// Resume are on each running task's sheet. Layout only; the E-stop is taskActions.emergencyStop.

const LASER = "Laser";
const CAMS = CAMERA_NAMES.Laser;

function LiveStream({ index }) {
  const imgRef = useRef(null);
  const retry = useRef(null);
  useEffect(() => () => clearTimeout(retry.current), []);
  return (
    <div className="jq-cam-full">
      <img
        ref={imgRef}
        src={streamUrl(LASER, index)}
        alt={`Laser ${CAMS[index] || `Camera ${index + 1}`}, live`}
        onError={(e) => {
          // A dropped MJPEG stream comes back with a fresh URL after 3 s (as the old phone page did).
          const el = e.currentTarget;
          clearTimeout(retry.current);
          retry.current = setTimeout(() => {
            const url = new URL(el.src);
            url.searchParams.set("ts", Date.now());
            el.src = url.toString();
          }, 3000);
        }}
      />
    </div>
  );
}

export default function LaserMonitor({ onChanged }) {
  const [consumables, setConsumables] = useState([]);
  const [fullCam, setFullCam] = useState(null);
  const [busy, setBusy] = useState(false);
  const runningRef = useRef(false);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      const data = await loadLaserConsumables();
      if (alive && Array.isArray(data)) setConsumables(data);
    };
    tick();
    const t = setInterval(() => { if (!document.hidden) tick(); }, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const run = async (action) => {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      const result = await action();
      if (result && result.reload) onChanged();
    } finally {
      runningRef.current = false;
    }
  };

  return (
    <div data-testid="laser-monitor">
      <div className="jq-cams" aria-label="Laser cameras">
        {CAMS.map((name, i) => (
          <button key={name} type="button" className="jq-cam" onClick={() => setFullCam(i)} aria-label={`Open the ${name} camera`}>
            <SnapshotImg src={snapshotUrl(LASER, i)} alt={`Laser ${name}`} authHeaders={getCfHeaders()} intervalMs={2000} />
            <span className="jq-cam-label">{name}</span>
          </button>
        ))}
      </div>

      <div className="d-grid gap-2 mb-3">
        <button type="button" className="btn btn-danger jq-estop" disabled={busy} onClick={() => run(() => emergencyStop({ station: LASER, ui: phoneUi, setBusy }))}>
          <i className="fas fa-exclamation-triangle me-2" aria-hidden="true" />REMOTE E-STOP
        </button>
        <button type="button" className="btn btn-success btn-lg fw-bold" disabled={busy} onClick={() => run(() => resetEmergencyStop({ station: LASER, ui: phoneUi, setBusy }))}>
          <i className="fas fa-power-off me-2" aria-hidden="true" />Reset system
        </button>
      </div>

      {consumables.length > 0 && (
        <div className="jq-consumables" aria-label="Laser consumables">
          {consumables.map((item, idx) => {
            const current = item.current_life ?? item.current ?? 0;
            const avg = item.avg_life ?? item.avg ?? 0;
            const warn = item.status === "Warning" || (avg > 0 && current >= avg * 0.9);
            return (
              <div key={item.label || idx} className={`jq-consumable${warn ? " warn" : ""}`}>
                <small>{item.label}</small>
                <strong>{Number(current).toFixed(1)}h / {avg > 0 ? Number(avg).toFixed(1) : "-"}h</strong>
              </div>
            );
          })}
        </div>
      )}

      <FullScreenPanel
        open={fullCam !== null}
        title={fullCam !== null ? `Laser · ${CAMS[fullCam]}` : ""}
        onClose={() => setFullCam(null)}
        testId="laser-camera-full"
        footer={
          <div className="jq-two-buttons">
            {CAMS.map((name, i) => (
              <button key={name} type="button" className={`btn btn-lg ${i === fullCam ? "btn-danger" : "btn-outline-secondary"}`} onClick={() => setFullCam(i)}>
                {name}
              </button>
            ))}
          </div>
        }
      >
        {fullCam !== null && <LiveStream key={fullCam} index={fullCam} />}
      </FullScreenPanel>
    </div>
  );
}
